import NotesHistoryButton from '../../components/NotesHistoryButton';
import { useEffect, useCallback } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Grid,
} from '@mui/material';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import { FormikTextField, FormikSwitch, FormikSelect, FormikTagSelect } from '../../components/FormikFields';
import { offersService } from '../../services/offers.service';
import { networksService } from '../../services/networks.service';
import { EntitySelectFieldFormik } from '../../components/EntitySelectField';

const REFERRER_POLICIES = [
  { value: '', label: 'Inherit from network' },
  { value: 'none', label: 'None (no referrer)' },
  { value: 'origin', label: 'Origin (domain only)' },
  { value: 'full', label: 'Full (complete URL)' },
] as const;

const REDIRECT_PAGES = [
  { value: 'inherit', label: 'Inherit from network' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
] as const;

interface OfferFormValues {
  name: string;
  notes: string;
  ref: string;
  url: string;
  network_id: string | null;
  postback_id: string | null;
  geo: string;
  traffic_cap_limit: number;
  report_to_source: boolean;
  referrer_policy: '' | 'none' | 'origin' | 'full';
  redirect_page: 'inherit' | 'on' | 'off';
  tag_ids: string[];
}

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  url: Yup.string().trim().required('URL is required'),
  network_id: Yup.string().nullable().required('Network is required'),
  geo: Yup.string().trim().default(''),
  postback_id: Yup.string().nullable(),
  referrer_policy: Yup.string().default(''),
  redirect_page: Yup.string().default('inherit'),
  tag_ids: Yup.array().of(Yup.string()).default([]),
  traffic_cap_limit: Yup.number().min(0).default(0),
  report_to_source: Yup.boolean().default(false),
});

export default function CreateEditOffer() {
  const { modalState, toggleModal } = useModalState('offer');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['offer', modalState.id],
    queryFn: () => offersService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });


  const isEdit = modalState.reason === 'Update' && !!entity;
  const isDuplicate = modalState.reason === 'Duplicate';
  const title = isEdit ? `Edit: ${entity?.name}` : isDuplicate ? 'Duplicate Offer' : 'Create Offer';

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const initialValues: OfferFormValues = (() => {
    if (!entity) return {
      name: '', notes: '', ref: '', url: '', geo: '',
      network_id: (modalState.defaults?.network_id as string) ?? null,
      postback_id: null as string | null,
      traffic_cap_limit: 0, report_to_source: false,
      referrer_policy: '' as const, redirect_page: 'inherit' as const, tag_ids: [],
    };
    const base: OfferFormValues = {
      name: entity.name,
      notes: entity.notes ?? '',
      ref: isDuplicate ? '' : (entity.ref ?? ''),
      url: entity.url,
      network_id: entity.network_id ?? null,
      postback_id: entity.postback_id ?? null,
      geo: entity.geo || '',
      traffic_cap_limit: entity.traffic_cap_limit || 0,
      report_to_source: entity.report_to_source,
      referrer_policy: entity.referrer_policy === 'none' || entity.referrer_policy === 'origin' || entity.referrer_policy === 'full' ? entity.referrer_policy : '',
      redirect_page: entity.use_redirect_page === true ? 'on' : entity.use_redirect_page === false ? 'off' : 'inherit',
      tag_ids: entity.tag_ids ?? [],
    };
    return base;
  })();

  // On a duplicate, if the picked network moves to another workspace, clear the (now-invalid) postback/tags
  // so the user sees what will be saved. Workspace itself is derived server-side from the network.
  const onNetworkChange = async (networkId: string | null, setFieldValue: (field: string, value: unknown) => void) => {
    if (!networkId || !isDuplicate || !entity) return;
    const network = await networksService.getById(networkId).catch(() => null);
    if (network && network.workspace_id !== (entity as any).workspace_id) {
      setFieldValue('postback_id', null);
      setFieldValue('tag_ids', []);
      enqueueSnackbar('Network is in another workspace — postback and tags were cleared (they can’t follow)', { variant: 'info' });
    }
  };

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="offer-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
                setSubmitted();
              const payload = {
                name: values.name,
                notes: values.notes,
                ref: values.ref,
                url: values.url,
                network_id: values.network_id,
                report_to_source: values.report_to_source,
                postback_id: values.postback_id,
                geo: values.geo || null,
                traffic_cap_limit: Number(values.traffic_cap_limit),
                referrer_policy: values.referrer_policy || null,
                use_redirect_page: values.redirect_page === 'inherit' ? null : values.redirect_page === 'on',
                tag_ids: values.tag_ids,
              };
              if (isEdit) {
                await offersService.update(entity!.id, payload);
              } else {
                await offersService.create(payload); // workspace derived server-side from the network
              }
              queryClient.invalidateQueries({ queryKey: ['offers'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, setFieldValue }: FormikProps<OfferFormValues>) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CopyId id={entity?.id} show={isEdit} />
                  <Box sx={{ flex: 1 }} />
                  {isEdit && <HistoryButton entityType="offers" entityId={entity!.id} entityName={entity?.name} />}
                </Box>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormikTextField name="name" label="Name" size="small" sx={{ flex: 1 }} />
                    <FormikTextField name="ref" label="Ref" size="small" placeholder="e.g. O1200" sx={{ width: { xs: '100%', sm: 160 } }} />
                  </Box>
                  <FormikTextField name="url" label="Destination URL" fullWidth size="small" />
                  <FormikTextField name="notes" label="Notes" fullWidth size="small" multiline rows={2}
                    slotProps={{ input: { endAdornment: <NotesHistoryButton entity="offer" id={isEdit ? modalState.id : undefined} /> } }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <FormikTextField
                        name="traffic_cap_limit"
                        label="Traffic Cap"
                        fullWidth
                        size="small"
                        type="number"
                        helperText="0 = unlimited"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    {!modalState.hideParent && (
                      <Grid size={{ xs: 6 }}>
                        <EntitySelectFieldFormik
                          name="network_id" field="network_id" label="Network" required
                          onChanged={(id) => onNetworkChange(id, setFieldValue)}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 6 }}>
                      <FormikSelect
                        name="referrer_policy" label="Referrer Policy" options={REFERRER_POLICIES}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <FormikSelect
                        name="redirect_page" label="Redirect page" options={REDIRECT_PAGES}
                        helperText="Serve a 200 redirect page at this offer (overrides the network setting)."
                      />
                    </Grid>
                  </Grid>
                  <EntitySelectFieldFormik
                    name="postback_id" field="postback_id" label="Postback (optional)"
                  />
                  <FormikSwitch name="report_to_source" label="Report conversions to traffic source" />
                  <FormikTagSelect name="tag_ids" />
                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
